import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { v4 as uuidv4 } from "uuid";

const DEFAULT_AVATAR = "";
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [lastLoginAt, setLastLoginAt] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(true);
  const [authInProgress, setAuthInProgress] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  // ✅ guardamos los unsubs para cerrar INMEDIATO (antes de signOut)
  const unsubProfileRef = useRef(null);
  const unsubMemberRoleRef = useRef(null);
  const unsubSessionWatchRef = useRef(null);

  const stopAllListeners = useCallback(() => {
    try {
      if (unsubProfileRef.current) {
        unsubProfileRef.current();
        unsubProfileRef.current = null;
      }
    } catch {}
    try {
      if (unsubMemberRoleRef.current) {
        unsubMemberRoleRef.current();
        unsubMemberRoleRef.current = null;
      }
    } catch {}
    try {
      if (unsubSessionWatchRef.current) {
        unsubSessionWatchRef.current();
        unsubSessionWatchRef.current = null;
      }
    } catch {}
  }, []);

  // ✅ ID único por navegador/instalación (ESTABLE)
  const getDeviceId = useCallback(() => {
    const key = "aa:deviceId";
    let id = localStorage.getItem(key);
    if (!id) {
      id = uuidv4();
      localStorage.setItem(key, id);
    }
    return id;
  }, []);

  const registrarSesion = useCallback(
    async (fbUser) => {
      try {
        // 🌐 Obtener IP pública (best-effort)
        let ip = "";
        try {
          const ipResp = await fetch("https://api.ipify.org?format=json");
          const ipJson = await ipResp.json();
          ip = ipJson?.ip || "";
        } catch {
          ip = "";
        }

        // 📱 Detectar navegador y dispositivo (simple)
        const userAgent = navigator.userAgent || "";
        let navegador = "Navegador";

        if (userAgent.includes("Edg/")) navegador = "Edge";
        else if (userAgent.includes("Chrome/") && !userAgent.includes("Edg/"))
          navegador = "Chrome";
        else if (
          userAgent.includes("Safari/") &&
          !userAgent.includes("Chrome/")
        )
          navegador = "Safari";
        else if (userAgent.includes("Firefox/")) navegador = "Firefox";

        let dispositivo = navegador;
        if (userAgent.includes("Windows"))
          dispositivo = `${navegador} en Windows`;
        else if (userAgent.includes("Mac")) dispositivo = `${navegador} en Mac`;
        else if (userAgent.includes("Android"))
          dispositivo = `${navegador} en Android`;
        else if (userAgent.includes("iPhone") || userAgent.includes("iPad"))
          dispositivo = `${navegador} en iOS`;

        const deviceId = getDeviceId();
        const sessionRef = doc(db, "users", fbUser.uid, "sessions", deviceId);

        await setDoc(
          sessionRef,
          {
            sessionId: deviceId,
            deviceId,
            dispositivo,
            userAgent,
            platform: navigator.platform || "",
            ...(ip ? { ip } : {}),
            ultimaActividad: serverTimestamp(),
            activo: true,
            eliminado: false,
            revokedAt: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );

        // Geo best-effort (no debe romper login si falla)
        try {
          const url = ip
            ? `https://us-central1-comercialapp-17b9c.cloudfunctions.net/geoFromIp?ip=${encodeURIComponent(ip)}`
            : `https://us-central1-comercialapp-17b9c.cloudfunctions.net/geoFromIp`;

          const res = await fetch(url, { method: "GET" });
          const geo = await res.json();

          const hasAnyGeo =
            !!(geo?.city && geo.city.trim()) ||
            !!(geo?.region && geo.region.trim()) ||
            !!(geo?.country && geo.country.trim());

          if (hasAnyGeo) {
            await updateDoc(sessionRef, {
              ...(geo?.ip ? { ip: geo.ip } : {}),
              ubicacion: {
                city: geo?.city || "",
                region: geo?.region || "",
                country: geo?.country || "",
              },
              updatedAt: serverTimestamp(),
            });
          }
        } catch (e) {
          console.warn("[geoFromIp AuthContext]", e);
        }

        localStorage.setItem(`sessionId:${fbUser.uid}`, deviceId);
        setSessionId(deviceId);
      } catch (error) {
        // 👇 si aquí da permission-denied, es porque auth ya cambió; no lo vueles.
        if (error?.code !== "permission-denied") {
          console.error("Error registrando sesión:", error);
        }
      }
    },
    [getDeviceId],
  );

  const persistAvatarUrl = useCallback(
    async (url) => {
      if (!user?.uid) return;
      await updateDoc(doc(db, "users", user.uid), {
        avatarUrl: url,
        updatedAt: serverTimestamp(),
      });
    },
    [user?.uid],
  );

  // ✅ Auth state principal
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (fbUser) => {
      // Antes de cambiar de usuario/estado: cerrar listeners viejos
      stopAllListeners();

      if (!fbUser) {
        setUser(null);
        setOrgId(null);
        setUserRole("");
        setLastLoginAt(null);
        setAvatarUrl(DEFAULT_AVATAR);
        setFirstName("");
        setLastName("");
        setSessionId(null);
        setLoading(false);
        localStorage.removeItem("sessionId"); // compatibilidad vieja
        return;
      }

      setUser(fbUser);
      setLoading(true);

      const uid = fbUser.uid;
      const userRef = doc(db, "users", uid);

      // 1) Registrar sesión (mejor await para evitar carreras)
      await registrarSesion(fbUser);

      // 2) Touch del user (si falla por permisos, no tumbar app)
      try {
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          await updateDoc(userRef, {
            lastLoginAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      } catch (e) {
        if (e?.code !== "permission-denied") {
          console.warn("[AuthContext userDoc touch]", e);
        }
      }

      // 3) Listener de perfil
      unsubProfileRef.current = onSnapshot(
        userRef,
        (snap) => {
          if (!snap.exists()) {
            // Si tu app requiere que exista el perfil, aquí podrías crearlo
            // pero en tu caso ya lo creas en Login/ensureUserAndOrg.
            setLoading(false);
            return;
          }

          const d = snap.data() || {};

          let nombre = d.nombre || "";
          let apellido = d.apellido || "";

          if ((!nombre || !apellido) && fbUser.displayName) {
            const [n, ...rest] = fbUser.displayName.trim().split(" ");
            if (!nombre) nombre = n;
            if (!apellido) apellido = rest.join(" ");
          }

          const toDate = (v) =>
            v?.toDate
              ? v.toDate()
              : typeof v === "number"
                ? new Date(v)
                : typeof v === "string"
                  ? new Date(v)
                  : null;

          setLastLoginAt(toDate(d.lastLoginAt));
          setAvatarUrl((d.avatarUrl || "").trim());
          setFirstName(nombre);
          setLastName(apellido);

          const nextOrgId = d.defaultOrgId || null;
          setOrgId(nextOrgId);

          setLoading(false);
        },
        (err) => {
          // ✅ Si auth cambia / logout, esto puede pasar: no lo trates como “error fatal”
          if (err?.code === "permission-denied") {
            setLoading(false);
            return;
          }
          console.error("Error escuchando perfil:", err);
          setLoading(false);
        },
      );
    });

    return () => {
      stopAllListeners();
      unsubscribeAuth();
    };
  }, [registrarSesion, stopAllListeners]);

  // ✅ Listener de rol (se maneja con ref para cortar inmediato)
  useEffect(() => {
    // cortar anterior siempre
    if (unsubMemberRoleRef.current) {
      unsubMemberRoleRef.current();
      unsubMemberRoleRef.current = null;
    }

    if (!user?.uid || !orgId) {
      setUserRole("");
      return;
    }

    const memberRef = doc(db, "orgs", orgId, "members", user.uid);

    unsubMemberRoleRef.current = onSnapshot(
      memberRef,
      (snap) => {
        const role = snap.exists() ? snap.data()?.role || "" : "";
        setUserRole(role || "Ventas");
      },
      (err) => {
        if (err?.code === "permission-denied") return;
        console.error("Error escuchando role member:", err);
        setUserRole("Ventas");
      },
    );

    return () => {
      if (unsubMemberRoleRef.current) {
        unsubMemberRoleRef.current();
        unsubMemberRoleRef.current = null;
      }
    };
  }, [user?.uid, orgId]);

  // ✅ Watch de ESTA sesión (revocación)
  useEffect(() => {
    if (unsubSessionWatchRef.current) {
      unsubSessionWatchRef.current();
      unsubSessionWatchRef.current = null;
    }

    if (!user?.uid || !sessionId) return;

    const ref = doc(db, "users", user.uid, "sessions", sessionId);

    unsubSessionWatchRef.current = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          localStorage.removeItem(`sessionId:${user.uid}`);
          setSessionId(null);
          signOut(auth);
          return;
        }

        const data = snap.data();
        const revoked = data?.activo === false || data?.eliminado === true;

        if (revoked) {
          localStorage.removeItem(`sessionId:${user.uid}`);
          setSessionId(null);
          signOut(auth);
        }
      },
      (err) => {
        if (err?.code === "permission-denied") return;
        console.error("Error escuchando sesión:", err);
      },
    );

    return () => {
      if (unsubSessionWatchRef.current) {
        unsubSessionWatchRef.current();
        unsubSessionWatchRef.current = null;
      }
    };
  }, [user?.uid, sessionId]);

  // ✅ Logout robusto: cortar listeners ANTES de signOut
  const logout = useCallback(async () => {
    const currentUser = auth.currentUser;
    const currentUid = currentUser?.uid || user?.uid || null;

    try {
      // 1) cortar listeners YA (evita el spam permission-denied)
      stopAllListeners();

      // 2) marcar sesión como revocada (best-effort)
      const key = currentUid ? `sessionId:${currentUid}` : null;
      const sid = sessionId || (key ? localStorage.getItem(key) : null);

      if (currentUid && sid) {
        try {
          await updateDoc(doc(db, "users", currentUid, "sessions", sid), {
            activo: false,
            eliminado: true,
            revokedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } catch (e) {
          if (e?.code !== "permission-denied") {
            console.warn("Error cerrando sesión remota:", e);
          }
        }
      }
    } finally {
      // 3) limpiar storage/estado ANTES de signOut reduce carreras
      localStorage.removeItem("inviteOrgId");
      localStorage.removeItem("mfaValidUntil");
      if (currentUid) localStorage.removeItem(`sessionId:${currentUid}`);

      setSessionId(null);
      setOrgId(null);
      setUserRole("");
      setLastLoginAt(null);
      setUser(null);

      // 4) signOut al final
      await signOut(auth);
    }
  }, [sessionId, stopAllListeners, user?.uid]);

  return (
    <AuthContext.Provider
      value={{
        user,
        orgId,
        sessionId,
        userRole,
        avatarUrl,
        firstName,
        lastName,
        loading,
        authInProgress,
        setAuthInProgress,
        setAvatarUrl,
        persistAvatarUrl,
        logout,
        lastLoginAt,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
